import { BaseError } from "viem";

/**
 * Detect connection errors by walking the viem cause chain.
 */
export function isConnectionError(err: unknown): boolean {
  const patterns = [
    "fetch failed",
    "ECONNREFUSED",
    "ERR_CONNECTION_REFUSED",
    "Failed to fetch",
    "NetworkError",
    "ETIMEDOUT",
  ];

  function matchesPattern(msg: string): boolean {
    return patterns.some((p) => msg.includes(p));
  }

  if (err instanceof BaseError) {
    // Walk the viem cause chain
    const leaf = err.walk();
    if (leaf.message && matchesPattern(leaf.message)) return true;
    if (matchesPattern(err.message)) return true;
    return false;
  }

  if (err instanceof Error) {
    return matchesPattern(err.message);
  }

  return matchesPattern(String(err));
}

/**
 * Extract a user-friendly one-liner from viem errors.
 *
 * Viem's `BaseError.message` is a multi-line dump with URLs, request bodies,
 * and calldata. `BaseError.shortMessage` is a clean one-liner.
 */
export function parseViemError(err: unknown): string {
  if (isConnectionError(err)) {
    return "Cannot connect to Anvil at 127.0.0.1:8545. Is it running?";
  }

  if (err instanceof BaseError) {
    // Walk to find revert reason
    const leaf = err.walk();
    if (leaf !== (err as unknown) && leaf.message) {
      const firstLine = leaf.message.split("\n")[0]?.trim() ?? "";
      if (firstLine.length > 0 && firstLine.length <= 120) {
        return firstLine;
      }
    }

    // shortMessage is a clean one-liner
    if (err.shortMessage) {
      return err.shortMessage;
    }
  }

  if (err instanceof Error) {
    // Take only the first line, capped at 120 chars
    const firstLine = err.message.split("\n")[0]?.trim() ?? "";
    if (firstLine.length > 120) {
      return firstLine.slice(0, 117) + "...";
    }
    return firstLine || "Unknown error";
  }

  return String(err) || "Unknown error";
}
